import { useId, useState, type ReactNode } from 'react';

import { useT, type Locale } from '../../i18n';
import type { Schluessel } from '../../platform/einstellungen';
import { useAppStore } from '../../store/useAppStore';
import { Icon } from '../../ui/Icon';
import { eintragsBeschreibung } from './einstellungsFelder';
import styles from './Sheets.module.css';

/**
 * Eine Einstellungszeile mit „?" daneben; ein Tipp klappt ihre Beschreibung auf.
 *
 * **Warum.** Seit 2026-10-02 tragen Gruppen keine Fussnote mehr (Entscheidung des Nutzers,
 * gemeinsam mit Ink und Relay). Die Erklärung steht bei der Einstellung selbst, als
 * `beschreibung` im Katalog — dieselbe, die Relays Doku-Seite als Tooltip zeigt.
 *
 * ⚠️ **Aufklappen, kein Tooltip.** Das iPad kennt kein Überfahren mit der Maus; ein Tooltip
 * käme dort nie zum Vorschein. Der Text steht deshalb unter der Zeile, bis man wieder tippt.
 */
export function MitErklaerung({
  name,
  locale,
  children,
}: {
  name: string;
  locale: Locale;
  children: ReactNode;
}) {
  const [offen, setOffen] = useState(false);
  const id = useId();
  const t = useT();
  const text = eintragsBeschreibung(name, locale);
  // Kommt der Wert aus der ausgewählten Prüfung (oder wurde für sie geändert), sagt das ein
  // Symbol — sonst wunderte man sich nach dem Prüfungswechsel über den anderen Wert.
  const quelle = useAppStore((s) => s.quellen[name as Schluessel]);
  const markeText =
    quelle === 'pruefung' ? t.settings.ausPruefung : quelle === 'appPruefung' ? t.settings.inPruefungGeaendert : null;
  const marke = markeText && (
    <span className={styles.pruefungsMarke} role="img" aria-label={markeText} title={markeText}>
      <Icon name={quelle === 'pruefung' ? 'exam' : 'pencil'} size={16} />
    </span>
  );
  if (!text) {
    return marke ? (
      <div className={styles.mitErklaerungZeile}>
        <div className={styles.mitErklaerungInhalt}>{children}</div>
        {marke}
      </div>
    ) : (
      <>{children}</>
    );
  }

  return (
    <div className={styles.mitErklaerung}>
      <div className={styles.mitErklaerungZeile}>
        <div className={styles.mitErklaerungInhalt}>{children}</div>
        {/* Als Plakette am „?“, nicht daneben: sonst würde die Zeile schmaler als die übrigen. */}
        <span className={styles.erklaerungMitMarke}>
        <button
          type="button"
          className={`tap ${styles.erklaerungKnopf}`}
          data-on={offen}
          aria-expanded={offen}
          aria-controls={id}
          aria-label={t.settings.erklaerung}
          onClick={() => setOffen((o) => !o)}
        >
          ?
        </button>
        {marke}
        </span>
      </div>
      {offen && (
        <div id={id} className={styles.erklaerungText}>
          {text}
        </div>
      )}
    </div>
  );
}
